/**
 * <ProductDetailDrawer sku={openSku} scope={scope} onClose={() => setOpenSku(null)} onPlatform={code => setScope({ platform: code })} />
 *
 * Right-side sheet with one SKU's sales for the page's period + comparison. Opens when `sku` is set.
 * Loads GET /api/sales-hub/detail with the scope's period, compare, country and platform (brand,
 * category and search filters are dropped: they describe the list, not this SKU).
 * Shows: product header, KPI row (revenue, units, avg price, discount, returns, stock + days of cover),
 * daily trend (bars = this period, dashed line = comparison, Revenue/Units toggle), platform split
 * (share, ASP, discount; clicking a platform calls onPlatform when given), stock by bin, and a
 * "View records" link to /daily-sales?sku=SKU&date_from=..&date_to=.. .
 * Skeleton while loading, LoadError with Retry on failure, a clear message for an unknown SKU.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Skeleton } from '../ui/skeleton';
import { LoadError } from '../LoadError';
import { platformColor, platformLabel } from '../../config/platforms';
import { useSalesData } from './salesApi';
import { DeltaBadge } from './DeltaBadge';
import { ShareBar } from './ShareBar';
import { compareWords, coverTone, dayCount, formatDateRange, number, shortMoney, wholeMoney } from './format';

import { useChannels } from '../../hooks/useChannels';
const COVER_STYLES = {
  danger: 'text-red-700 bg-red-50 ring-red-200',
  warning: 'text-amber-800 bg-amber-50 ring-amber-200',
  success: 'text-green-800 bg-green-50 ring-green-200',
  neutral: 'text-gray-700 bg-gray-100 ring-gray-200',
};
const pctText = value => (value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`);
const shortDate = day => `${Number(day.slice(8, 10))}/${Number(day.slice(5, 7))}`;

const Stat = ({ label, value, children, testId }) => (
  <div className="min-w-0 rounded-lg border border-gray-200 p-3" data-testid={testId}>
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-0.5 truncate text-lg font-semibold tabular-nums text-gray-900">{value}</div>
    {children && <div className="mt-1 text-xs text-gray-600">{children}</div>}
  </div>
);

const DrawerSkeleton = () => (
  <div className="space-y-4" aria-busy="true" aria-label="Loading product sales">
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[74px]" />)}</div>
    <Skeleton className="h-56" />
    <Skeleton className="h-32" />
  </div>
);

const TrendChart = ({ trend, metric }) => {
  const isMoney = metric === 'revenue';
  const fmt = value => (isMoney ? shortMoney(value) : number(value));
  return (
    <div role="img" aria-label={`Daily ${metric} for this period, dashed line shows the comparison period`} className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={trend} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis width={56} tickFormatter={fmt} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip labelFormatter={label => formatDateRange(label, label, true)} formatter={(value, name) => [fmt(value), name]} />
          <Bar dataKey={metric} name="This period" fill="#2563EB" radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false} />
          <Line dataKey={`previous_${metric}`} name="Comparison" stroke="#94A3B8" strokeDasharray="5 4" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const ProductDetailDrawer = ({ sku, scope, onClose, onPlatform }) => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  // Keep the last SKU while the sheet animates closed so the content does not blank out.
  const [shownSku, setShownSku] = useState(sku || null);
  useEffect(() => { if (sku) setShownSku(sku); }, [sku]);
  const [metric, setMetric] = useState('revenue');
  const open = Boolean(sku);
  // Only fetch while open; useSalesData keeps the last data when the path goes null, so the
  // closing animation still has content but later scope changes do not re-fetch a closed drawer.
  const { data, loading, error, notFound, refresh } = useSalesData(open && shownSku ? 'detail' : null, scope, {
    sku: shownSku, search: null, brand: null, category: null,
  });
  const fresh = data && String(data.sku_code || '').toLowerCase() === String(shownSku || '').toLowerCase() ? data : null;
  const days = scope ? dayCount(scope.date_from, scope.date_to) : 1;
  const vs = compareWords(scope?.compare, days);
  const summary = fresh?.summary || {};
  const trend = fresh?.trend || [];
  const totalRevenue = (fresh?.platforms || []).reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
  const platforms = (fresh?.platforms || []).filter(item => (Number(item.revenue) || 0) > 0 || (Number(item.units) || 0) > 0)
    .sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
  const bins = Object.entries(summary.stock_by_bin || {}).filter(([, units]) => Number(units)).sort((a, b) => b[1] - a[1]);
  const tone = coverTone(summary.days_of_cover);
  const hasSales = trend.some(row => (row.revenue || 0) > 0 || (row.units || 0) > 0);
  const recordsQuery = scope ? new URLSearchParams({
    sku: shownSku || '', date_from: scope.date_from, date_to: scope.date_to,
    ...(scope.platform && scope.platform !== 'ALL' ? { platform: scope.platform } : {}),
  }).toString() : '';

  return (
    <Sheet open={open} onOpenChange={next => { if (!next) onClose?.(); }}>
      <SheetContent side="right" className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-xl" data-testid="product-detail-drawer">
        <SheetHeader className="shrink-0 border-b border-gray-200 bg-white px-5 py-4 pr-12 text-left">
          <SheetTitle className="text-base leading-snug">{fresh?.product_name || shownSku || 'Product'}</SheetTitle>
          <SheetDescription className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
            <span className="font-mono text-gray-700">{shownSku}</span>
            {fresh?.brand && <span>· {fresh.brand}</span>}
            {fresh?.category && <span>· {fresh.category}</span>}
            {fresh?.list_price ? <span>· List {wholeMoney(fresh.list_price)}</span> : null}
          </SheetDescription>
          {scope && (
            <p className="text-xs text-gray-500">
              {formatDateRange(scope.date_from, scope.date_to, true)} · {vs.replace(/^vs /, 'compared with ')}
              {scope.platform && scope.platform !== 'ALL' ? ` · ${platformLabel(scope.platform)} only` : ''}
            </p>
          )}
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {notFound ? (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700" data-testid="product-detail-not-found">
              This SKU is not in the catalogue for this country. Check the code or switch country.
            </p>
          ) : error && !fresh ? (
            <LoadError what="this product's sales" error={error} onRetry={refresh} retrying={loading} testId="product-detail-error" />
          ) : !fresh ? (
            <DrawerSkeleton />
          ) : (
            <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
              {error && <LoadError what="the latest numbers" error={error} onRetry={refresh} retrying={loading} />}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="product-detail-kpis">
                <Stat label="Revenue" value={shortMoney(summary.revenue)} testId="product-detail-revenue">
                  <DeltaBadge current={summary.revenue} previous={summary.previous_revenue} size="xs" />
                </Stat>
                <Stat label="Units" value={number(summary.units)}>
                  <DeltaBadge current={summary.units} previous={summary.previous_units} kind="number" size="xs" />
                </Stat>
                <Stat label="Avg selling price" value={summary.avg_selling_price ? wholeMoney(summary.avg_selling_price) : '—'}>
                  {fresh.list_price ? `List ${wholeMoney(fresh.list_price)}` : 'No list price'}
                </Stat>
                <Stat label="Avg discount" value={pctText(summary.avg_discount_pct)}>off list price</Stat>
                <Stat label="Returns" value={number(summary.returned_units)}>{pctText(summary.return_rate)} of units</Stat>
                <Stat label="Stock" value={summary.stock == null ? '—' : number(summary.stock)} testId="product-detail-stock">
                  <span className={`inline-flex rounded-full px-1.5 py-0 text-[11px] font-medium ring-1 ring-inset ${COVER_STYLES[tone]}`}
                    title={summary.days_of_cover == null ? 'No sales in this period, so cover cannot be estimated' : 'Days the current stock lasts at this period\'s daily sales'}>
                    {summary.days_of_cover == null ? 'Cover unknown' : `${number(summary.days_of_cover)} days cover`}
                  </span>
                </Stat>
              </div>

              <section aria-labelledby="pdd-trend">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 id="pdd-trend" className="text-sm font-semibold text-gray-900">Daily trend</h3>
                  <div role="group" aria-label="Trend metric" className="inline-flex rounded-lg bg-gray-100 p-0.5">
                    {[['revenue', 'Revenue'], ['units', 'Units']].map(([key, label]) => (
                      <button key={key} type="button" aria-pressed={metric === key} onClick={() => setMetric(key)}
                        className={`h-7 rounded-md px-2.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${metric === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {hasSales ? <TrendChart trend={trend} metric={metric} /> : (
                  <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">No sales for this SKU in the period. Try a longer period or another platform.</p>
                )}
              </section>

              <section aria-labelledby="pdd-platforms">
                <h3 id="pdd-platforms" className="mb-1 text-sm font-semibold text-gray-900">Platform split</h3>
                {platforms.length ? (
                  <div className="space-y-1">
                    {platforms.map(item => {
                      const name = item.label || platformLabel(item.platform);
                      const sub = [
                        `${number(item.units)} units`,
                        item.avg_selling_price ? `ASP ${wholeMoney(item.avg_selling_price)}` : null,
                        item.avg_discount_pct != null ? `${pctText(item.avg_discount_pct)} off` : null,
                        item.returned_units ? `${number(item.returned_units)} returned` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <ShareBar key={item.platform} label={name} value={shortMoney(item.revenue)} sublabel={sub}
                          share={totalRevenue ? (item.revenue / totalRevenue) * 100 : 0} color={platformColor(item.platform)}
                          onClick={onPlatform ? () => { onPlatform(item.platform); onClose?.(); } : undefined}
                          ariaLabel={onPlatform ? `Filter the page to ${name}` : undefined} testId={`product-detail-platform-${item.platform}`} />
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-gray-600">No platform sales in this period.</p>}
              </section>

              <section aria-labelledby="pdd-bins">
                <h3 id="pdd-bins" className="mb-2 text-sm font-semibold text-gray-900">Stock by bin</h3>
                {bins.length ? (
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                    {bins.map(([bin, units]) => (
                      <li key={bin} className="flex justify-between gap-2 px-3 py-1.5"><span className="truncate text-gray-700">{bin}</span><span className="tabular-nums">{number(units)}</span></li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-gray-600">No stock recorded in any bin. Update stock on the inventory page.</p>}
              </section>

              <Link to={`/daily-sales?${recordsQuery}`} onClick={() => onClose?.()}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                data-testid="product-detail-records-link">
                View records <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ProductDetailDrawer;
